// Pure helpers for the P7 informal care + meals UI (spec §13). Everything
// here is deterministic and side-effect free so the Playwright no-deps spec
// can exercise the exact payloads/state derivations the RPCs consume.
//
// Mirrors the deployed contracts in
// reachchms-functions/supabase/schemas/functions/
//   public_follow_up_start_care.sql           (context + careDetails jsonb)
//   public_follow_up_update_care_details.sql  (RFC 7396 merge-patch)
//   public_follow_up_set_meal_dates.sql       (enable/edit/disable + impact)
//   public_follow_up_claim_meal.sql           (capacity-one claim)
//   public_follow_up_close_care.sql           (explicit keep/cancel preview)
// and tables/public_FollowUpMealSlots.sql + public_FollowUpCareEpisodes.sql.
//
// Relative imports (not '@/'-aliased) so Playwright specs can import this
// module directly through Node resolution — same convention as
// followUpPaths.js.

import {
    FOLLOW_UP_MEAL_SLOT_STATES,
    FOLLOW_UP_CARE_DETAIL_KEYS,
} from './constants/followUp';

// ---------------------------------------------------------------------------
// Meal slot state derivation (C10 — claimed is never delivered).
// ---------------------------------------------------------------------------

/**
 * Derive the single display state for a meal slot row.
 * slot.status is OPEN/CANCELLED (the need). Delivered derives from the
 * linked task completing (delivered/deliveredAt); claimed is a staffed
 * commitment that is NOT a delivery.
 * @returns 'DELIVERED' | 'CLAIMED' | 'OPEN' | 'CANCELLED'
 */
export const getMealSlotState = (slot) => {
    if (!slot || typeof slot !== 'object') return 'OPEN';
    if (slot.status === 'CANCELLED' || slot.cancelledAt) return 'CANCELLED';
    if (slot.delivered === true || !!slot.deliveredAt) return 'DELIVERED';
    if (slot.staffed === true || !!slot.assigneePersonID) return 'CLAIMED';
    return 'OPEN';
};

/** Display meta for a derived slot state (icon/label/colorVariant). */
export const getMealSlotMeta = (slot) =>
    FOLLOW_UP_MEAL_SLOT_STATES[getMealSlotState(slot)] || FOLLOW_UP_MEAL_SLOT_STATES.OPEN;

/** True when the slot can still take a volunteer (open + unstaffed). */
export const mealSlotIsClaimable = (slot) => getMealSlotState(slot) === 'OPEN';

// ---------------------------------------------------------------------------
// Dates — one needed meal per 'YYYY-MM-DD' date key (C05).
// ---------------------------------------------------------------------------

/**
 * Format a Date as a local 'YYYY-MM-DD' meal-date key. Never use
 * toISOString().slice(0,10) — that shifts the day in non-UTC timezones.
 */
export const toMealDateKey = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalize a mixed list of Date/'YYYY-MM-DD' inputs into a sorted,
 * deduplicated list of valid 'YYYY-MM-DD' keys (the C05 date key). Invalid
 * entries are dropped — the server still validates authoritatively.
 */
export const normalizeMealDates = (dates) => {
    const keys = new Set();
    (dates || []).forEach((d) => {
        if (!d) return;
        if (d instanceof Date) {
            const key = toMealDateKey(d);
            if (key) keys.add(key);
            return;
        }
        const s = `${d}`.trim();
        if (DATE_KEY_RE.test(s) && !Number.isNaN(new Date(`${s}T00:00:00`).getTime())) {
            keys.add(s);
        }
    });
    return [...keys].sort();
};

/** Short display for a 'YYYY-MM-DD' meal date — 'Mon, Mar 4'. */
export const formatMealDate = (dateKey) => {
    if (!dateKey) return '';
    const d = new Date(`${dateKey}T00:00:00`);
    if (Number.isNaN(d.getTime())) return `${dateKey}`;
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// ---------------------------------------------------------------------------
// Payload builders — the exact shapes the RPCs validate.
// ---------------------------------------------------------------------------

/**
 * Build the careDetails jsonb for follow_up_start_care from the intake
 * form (spec §25.9/§7.5). Only the documented keys are kept; empty strings
 * are dropped. Returns null when nothing was entered — callers then omit
 * the careDetails key entirely.
 */
export const buildCareDetails = (values = {}) => {
    const details = {};
    FOLLOW_UP_CARE_DETAIL_KEYS.forEach((key) => {
        const raw = values?.[key];
        if (raw == null) return;
        const s = `${raw}`.trim();
        if (s) details[key] = s;
    });
    return Object.keys(details).length ? details : null;
};

/**
 * Build the RFC 7396 merge-patch for follow_up_update_care_details by
 * diffing the stored careDetails against the edited form values
 * (spec §7.5/§13.3). Only the documented keys are considered: a key whose
 * value actually changed lands in the patch; an edited-empty key that was
 * previously set serializes as null so the server DELETES it (top-level
 * JSON null = delete); unchanged keys are omitted entirely and unknown
 * keys never reach the patch.
 * @returns {object} possibly {} — the server accepts it as a valid no-op,
 *   but callers should skip the RPC and close the editor instead.
 */
export const buildCareDetailsPatch = (original = {}, edited = {}) => {
    const patch = {};
    FOLLOW_UP_CARE_DETAIL_KEYS.forEach((key) => {
        const prevRaw = original?.[key];
        const nextRaw = edited?.[key];
        const prev = prevRaw == null ? '' : `${prevRaw}`.trim();
        const next = nextRaw == null ? '' : `${nextRaw}`.trim();
        if (next === prev) return;
        patch[key] = next === '' ? null : next;
    });
    return patch;
};

/**
 * Build the p_payload for follow_up_set_meal_dates (§25.9 — careEpisodeID,
 * not a Path enrollment). Enabling requires at least one date (C04) — an
 * empty enable is returned as an error so the UI never fires a call the
 * server must reject.
 * @returns {{errors: string[], payload: object|null}}
 */
export const buildMealDatesPayload = ({
    careEpisodeID,
    enabled = true,
    dates = [],
    deliveryInstructions = null,
    deliveryWindow = null,
    preview = false,
    confirmImpact = false,
} = {}) => {
    const errors = [];
    if (!careEpisodeID) errors.push('An active care episode is required.');
    const normalized = normalizeMealDates(dates);
    if (enabled && normalized.length === 0) {
        errors.push('Enabling meals requires at least one date.');
    }
    if (errors.length) return { errors, payload: null };

    const payload = { careEpisodeID };
    if (enabled === false) payload.enabled = false;
    if (normalized.length) payload.dates = normalized;
    const instructions = `${deliveryInstructions ?? ''}`.trim();
    if (instructions) payload.deliveryInstructions = instructions;
    const window = `${deliveryWindow ?? ''}`.trim();
    if (window) payload.deliveryWindow = window;
    if (preview) payload.preview = true;
    if (confirmImpact) payload.confirmImpact = true;
    return { errors: [], payload };
};

// ---------------------------------------------------------------------------
// Close-care preview decisions (§13.6/C12-C13).
// ---------------------------------------------------------------------------

/**
 * The default decision map for a close-care preview: every pending item is
 * KEPT — the spec default is preserve, so nothing starts pre-selected for
 * cancellation. The user makes an explicit per-item choice.
 * @returns {{[taskInstanceID]: 'keep'}}
 */
export const initialCloseDecisions = (pendingWork = []) =>
    (pendingWork || []).reduce((acc, item) => {
        if (item?.taskInstanceID) acc[item.taskInstanceID] = 'keep';
        return acc;
    }, {});

/** Toggle one item between 'keep' and 'cancel'. Unknown ids default to keep. */
export const toggleCloseDecision = (decisions, taskInstanceID) => ({
    ...(decisions || {}),
    [taskInstanceID]: decisions?.[taskInstanceID] === 'cancel' ? 'keep' : 'cancel',
});

/** Count of items marked for cancellation — drives the confirm copy. */
export const countCloseCancels = (decisions = {}) =>
    Object.values(decisions || {}).filter((d) => d === 'cancel').length;

/**
 * Build the p_payload for follow_up_close_care from the preview + the
 * decision map. cancel ids go first (explicit cancellations); keep ids list
 * every OTHER pending item so an item that appeared between preview and
 * apply is also kept by default (§13.6). Overlaps are impossible by
 * construction — the server rejects them as a belt-and-suspenders check.
 */
export const buildCloseCarePayload = ({
    careEpisodeID,
    pendingWork = [],
    decisions = {},
    closureNote = null,
    idempotencyKey = null,
} = {}) => {
    const pending = (pendingWork || [])
        .map((item) => item?.taskInstanceID)
        .filter(Boolean);
    const cancelTaskInstanceIDs = pending.filter((id) => decisions?.[id] === 'cancel');
    const keepTaskInstanceIDs = pending.filter((id) => decisions?.[id] !== 'cancel');

    const payload = { careEpisodeID };
    const note = `${closureNote ?? ''}`.trim();
    if (note) payload.closureNote = note;
    if (cancelTaskInstanceIDs.length) payload.cancelTaskInstanceIDs = cancelTaskInstanceIDs;
    if (keepTaskInstanceIDs.length) payload.keepTaskInstanceIDs = keepTaskInstanceIDs;
    if (idempotencyKey) payload.idempotencyKey = idempotencyKey;
    return { errors: careEpisodeID ? [] : ['An active care episode is required.'], payload: careEpisodeID ? payload : null };
};

/**
 * Impact rows from a set_meal_dates preview/apply response, each with a
 * human label. The server already classifies each impacted slot — the UI
 * names what will happen so a staffed or delivered commitment is never
 * silently erased (§13.5/C11).
 */
export const describeMealImpact = (impact = []) =>
    (impact || []).map((item) => ({
        ...item,
        description: item?.action === 'cancel_slot_keep_delivered'
            ? 'Already delivered. The delivery record stays and the date frees up'
            : item?.action === 'cancel_assigned'
                ? `Assigned${item?.assigneePersonID ? '. The volunteer is released' : ''}`
                : 'Open and unclaimed',
    }));

/**
 * Summary line for an applied close-care result (spec §13.6 — the response
 * always echoes what was pending at decision time, including work that
 * completed mid-review, C13).
 */
export const summarizeCloseResult = (result = {}) => {
    const cancelled = (result?.cancelledTaskInstanceIDs || []).length;
    const kept = (result?.keptTaskInstanceIDs || []).length;
    const skipped = result?.skippedTaskDecisions || [];
    const skippedCompleted = skipped.filter((s) => s?.skipped === 'completed').length;
    const parts = [];
    if (cancelled) parts.push(`${cancelled} cancelled`);
    if (kept) parts.push(`${kept} kept open`);
    if (skippedCompleted) parts.push(`${skippedCompleted} completed just now and kept`);
    if (skipped.length - skippedCompleted > 0) parts.push(`${skipped.length - skippedCompleted} skipped`);
    return parts.join(' · ');
};
